/**
 * Правка учёбки с доски ОО (13.10, владелец: «учёбкам в расстановке у ОО нужно иметь
 * возможность отредактировать»). Открывается ✎ на карточке учёбки. Поля — время, тема,
 * филиал, куратор и его ставка. Состав учеников правится на самой карточке, как раньше
 * («+», смахнуть чип). Дату не правим (13.10): на другой день — отменить и создать заново.
 * Классы шита — как в DopWorkSheet; план запросов и их порядок — hooks/studyEdit.ts.
 */
import { useRef, useState } from "react";
import { api } from "../../api/client";
import type { BoardOperator } from "../../hooks/useOoDay";
import type { ExtraWorkGroup } from "../../hooks/useExtraWork";
import { notCurator, planIsEmpty, planStudyEdit, runStudyEdit, studyFormOf, withCurator, type StudyForm } from "../../hooks/studyEdit";
import { NumPadPop, TextPad, TimePicker } from "../../components/shared";
import { CuratorPick } from "./CuratorPick";
import css from "./board.module.css";
import sx from "./studyEdit.module.css";

export interface StudyEditCtx {
  /** YYYY-MM-DD — день доски (новому куратору). */
  date: string;
  branches: string[];
  /** Все карточки дня: новый слот не должен совпасть с другой учёбкой. */
  groups: ExtraWorkGroup[];
  /** Итог правки — плашкой доски; доска перечитывается. */
  onDone: (msg: string) => void;
}

export function StudyEditSheet({ group, operators, date, branches, groups, onDone, onClose }: StudyEditCtx & {
  group: ExtraWorkGroup;
  operators: BoardOperator[];
  onClose: () => void;
}) {
  const [f, setF] = useState<StudyForm>(() => studyFormOf(group));
  const [err, setErr] = useState<string | null>(null);
  const set = (p: Partial<StudyForm>) => { setF((v) => ({ ...v, ...p })); setErr(null); };
  const [timeOpen, setTimeOpen] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const rateRef = useRef<HTMLButtonElement>(null);
  const [rateTyped, setRateTyped] = useState(false);   // ставку ОО набрал в окне — смена куратора её не сбрасывает
  const [busy, setBusy] = useState(false);
  /* 13.10: пока запросы идут, окно не закрываем — иначе можно открыть заново и повторить (второй куратор) */
  const close = () => { if (!busy) onClose(); };

  const trainees = group.ops.filter((o) => o.kind === "training");
  /* куратор может не числиться среди операторов доски — показываем и его, иначе выбор «пропадёт» */
  const cur = group.ops.find((o) => o.kind === "training_curator");
  const ops = cur && !operators.some((o) => o.id === cur.operatorId) ? [{ id: cur.operatorId, name: cur.operatorName }, ...operators] : operators;
  const brs = group.branchId && !branches.includes(group.branchId) ? [...branches, group.branchId] : branches;
  const nameOf = (id: string) => ops.find((o) => o.id === id)?.name ?? id;

  async function save() {
    const plan = planStudyEdit(group, f, { date, groups, nameOf });
    if ("error" in plan) { setErr(plan.error); return; }
    if (planIsEmpty(plan)) { onClose(); return; }
    setBusy(true);
    const res = await runStudyEdit(plan, (path, opts) => api(path, opts));
    setBusy(false);
    onClose();
    onDone(res.msg);
  }

  return (
    <>
      <div className={css.sheetBackdrop} onClick={close} />
      <div className={css.autoSheet} role="dialog" aria-modal="true" aria-label="Правка учёбки">
        <div className={css.asHandle} />
        <div className={css.asHead}>
          <span className={css.asTitle}>Правка учёбки</span>
          <button type="button" className={css.asClose} onClick={close} aria-label="Закрыть">✕</button>
        </div>
        <div className={css.asBody}>
          <div className={css.asLbl}>Время</div>
          <button type="button" className={css.dwTimeBtn} onClick={() => setTimeOpen(true)}>{f.timeFrom}–{f.timeTo}</button>

          <div className={css.asLbl}>Тема</div>
          <button type="button" className={sx.topic} onClick={() => setDescOpen(true)}>{f.description || "—"}</button>

          <div className={css.asLbl}>Филиал</div>
          <div className={css.dwChips}>
            <button type="button" className={`${css.dwChip} ${!f.branchId ? css.on : ""}`} onClick={() => set({ branchId: null })}>любой</button>
            {brs.map((b) => (
              <button key={b} type="button" className={`${css.dwChip} ${f.branchId === b ? css.on : ""}`} onClick={() => set({ branchId: b })}>{b}</button>
            ))}
          </div>

          <CuratorPick ops={ops} trainees={notCurator(group, groups, f)} value={f.curatorId}
            onChange={(id) => { setF((v) => withCurator(v, group, id, rateTyped)); setErr(null); }} />
          {f.curatorId ? (
            <>
              <div className={css.asLbl}>Ставка куратора</div>
              <button ref={rateRef} type="button" className={css.dwTimeBtn} onClick={() => setRateOpen(true)}>
                {f.curatorRate != null ? `${f.curatorRate} ₽/ч` : "по ставке сотрудника ›"}
              </button>
            </>
          ) : null}

          <div className={css.asLbl}>Ученики · {trainees.length}<span className={css.asLblHint}> — состав правится на карточке</span></div>
          <div className={sx.who}>{trainees.map((o) => o.operatorName).join(", ") || "—"}</div>

          {err ? <div className={css.actHint}>{err}</div> : null}
        </div>
        <div className={css.asFoot}>
          <button type="button" className={css.asRun} onClick={() => void save()} disabled={busy}>
            {busy ? "Сохраняю…" : "Сохранить изменения"}
          </button>
          <div className={sx.note}>{group.draft ? "Учёбка не отправлена — сообщений не будет" : "Участникам обновится сообщение в Telegram"}</div>
        </div>
      </div>
      {/* 09.09 (владелец): наша клавиатура вместо системной — как у темы на карточке */}
      <TextPad open={descOpen} label="Тема учёбки" multiline maxLength={200} value={f.description}
        onCommit={(v) => set({ description: v })} onClose={() => setDescOpen(false)} />
      {timeOpen ? (
        <TimePicker
          open mode="range" step={5} label="Учёбка — время от и до" value={`${f.timeFrom}–${f.timeTo}`}
          onCommit={(v) => { const m = v.match(/^(\d{2}:\d{2})[–-](\d{2}:\d{2})$/); if (m) set({ timeFrom: m[1]!, timeTo: m[2]! }); setTimeOpen(false); }}
          onCancel={() => setTimeOpen(false)}
        />
      ) : null}
      <NumPadPop
        open={rateOpen} anchor={rateRef.current} label="Ставка куратора ₽/ч" mode="money" value={f.curatorRate ?? 0} max={99999}
        onCommit={(v) => { if (v !== (f.curatorRate ?? 0)) { set({ curatorRate: v }); setRateTyped(true); } }} onClose={() => setRateOpen(false)}
      />
    </>
  );
}
